import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';

interface QueuedRequest<T> {
    url: string;
    resolve: (value: T) => void;
    reject: (error: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class LazyLoadQueueService {
    private readonly _queue$ = new Subject<QueuedRequest<unknown>>();
    private readonly _cache = new Map<string, unknown>();
    private readonly _pendingRequests = new Map<string, Promise<unknown>>();

    constructor(private readonly _http: HttpClient) {
        this._queue$
            .pipe(
                concatMap((request) => this._processRequest(request))
            )
            .subscribe();
    }

    load<T>(url: string): Promise<T> {
        // If we have cached data, return it immediately
        if (this._cache.has(url)) {
            return Promise.resolve(this._cache.get(url) as T);
        }

        // If there's already a pending request for this URL, return that promise
        if (this._pendingRequests.has(url)) {
            return this._pendingRequests.get(url) as Promise<T>;
        }

        // Create new request
        const promise = new Promise<T>((resolve, reject) => {
            this._queue$.next({
                url,
                resolve: resolve as (value: unknown) => void,
                reject,
            });
        });

        this._pendingRequests.set(url, promise);
        return promise;
    }

    private _processRequest<T>(request: QueuedRequest<T>): Promise<void> {
        return this._http
            .get<T>(request.url)
            .toPromise()
            .then((response) => {
                this._cache.set(request.url, response);
                this._pendingRequests.delete(request.url);
                request.resolve(response);
            })
            .catch((err) => {
                this._pendingRequests.delete(request.url);
                request.reject(err);
            });
    }
}
